import React from "react";
import { useQuery } from "@apollo/client";
import {
  Alert,
  AlertTitle,
  alpha,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Link,
  Paper,
  Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import {
  Link as RouterLink,
  useParams,
  useSearchParams,
} from "react-router-dom";

import { describeError } from "../../common/rateLimit";
import { contactPath } from "../crm/crmPaths";
import { formatDay, formatMoment } from "../crm/contactDetail/contactDetail";
import { sinceText } from "../crm/manage/manage";
import { Section, StatTile, TileRow } from "../reports/reportParts";
import { UserActivityFeed } from "./UserActivityFeed";
import {
  UserBadges,
  UserPoints,
  UserRecommendations,
  UserReferrals,
  UserReviews,
  UserWhatsApp,
} from "./UserLists";
import { USER_DETAILS_PAGE, USER_SUMMARY } from "./userDetails.queries";
import {
  accountChips,
  countOf,
  humanize,
  initialsOf,
  locationText,
  nameOf,
  parseTab,
  parseUserId,
  USER_TABS,
  UserTab,
} from "./userDetailsModel";

const n = (value: number | null | undefined) =>
  (value ?? 0).toLocaleString("en-US");

/** Labels and values in two columns. A missing value shows a dash rather than leaving a hole. */
const Facts: React.FC<{ rows: Array<[string, React.ReactNode]> }> = ({
  rows,
}) => (
  <Box
    component="dl"
    sx={{
      display: "grid",
      gridTemplateColumns: "minmax(110px, 38%) 1fr",
      columnGap: 2,
      rowGap: 0.75,
      m: 0,
      mt: 1,
    }}
  >
    {rows.map(([label, value]) => (
      <React.Fragment key={label}>
        <Typography component="dt" variant="body2" color="text.secondary">
          {label}
        </Typography>
        <Typography
          component="dd"
          variant="body2"
          sx={{ m: 0, wordBreak: "break-word" }}
        >
          {value === null || value === undefined || value === "" ? "—" : value}
        </Typography>
      </React.Fragment>
    ))}
  </Box>
);

const Verified: React.FC<{ value?: string | null; verified?: boolean }> = ({
  value,
  verified,
}) =>
  value ? (
    <>
      {value}{" "}
      <Typography
        component="span"
        variant="caption"
        color={verified ? "success.main" : "text.secondary"}
      >
        {verified ? "verified" : "not verified"}
      </Typography>
    </>
  ) : null;

/** Two columns stacked on their own, so a short card leaves no hole beside a tall one. */
const Overview: React.FC<{ user: any; summary: any }> = ({ user, summary }) => (
  <Box
    sx={{
      display: "grid",
      gap: 2,
      gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
      alignItems: "start",
    }}
  >
    <Stack spacing={2}>
      <Section title="Profile">
        <Facts
          rows={[
            [
              "Email",
              <Verified value={user.email} verified={user.isEmailVerified} />,
            ],
            [
              "Phone",
              <Verified value={user.phone} verified={user.isPhoneVerified} />,
            ],
            ["Location", locationText(user)],
            ["Street address", user.streetAddress],
            ["Gender", humanize(user.gender)],
            ["T-shirt size", user.tShirtSize],
          ]}
        />
      </Section>

      <Section title="Using the app">
        <Facts
          rows={[
            ["Last seen", formatMoment(summary?.lastSeenAt)],
            ["App opens", n(summary?.appOpens)],
            ["Active days", `${n(summary?.activeDaysLast30)} in the last 30`],
            [
              "Login streak",
              `${n(user.currentLoginStreak)} now, ${n(user.longestLoginStreak)} at most`,
            ],
            ["Committees", n(summary?.chatGroups)],
            ["Chat messages", n(summary?.chatMessagesLogged)],
            ["Feedback sent", n(summary?.feedbackSent)],
          ]}
        />
        {summary?.topScreens?.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
              Most viewed screens
            </Typography>
            <Stack
              component="ol"
              spacing={0.5}
              sx={{ listStyle: "none", m: 0, p: 0 }}
            >
              {summary.topScreens.map((item: any) => (
                <Stack
                  key={item.screen}
                  component="li"
                  direction="row"
                  justifyContent="space-between"
                  spacing={2}
                >
                  <Typography variant="body2">{item.screen}</Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontVariantNumeric: "tabular-nums" }}
                  >
                    {n(item.views)}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          </Box>
        )}
      </Section>
    </Stack>

    <Stack spacing={2}>
      <Section title="Account">
        <Facts
          rows={[
            ["Joined", formatDay(user.createdAt)],
            [
              "Referred by",
              summary?.referredBy && (
                <Link
                  component={RouterLink}
                  to={`/users/${summary.referredBy.id}`}
                  underline="hover"
                >
                  {summary.referredBy.name}
                </Link>
              ),
            ],
            ["Level", user.level],
            ["Member type", humanize(user.membershipType)],
            ["Phone verified", formatDay(user.phoneVerifiedAt)],
            ["Sign-up IP", user.latestIpAddress],
            ["User id", user.id],
            ["Firebase uid", user.uid],
          ]}
        />
      </Section>

      <Section title="Reaching them">
        <Facts
          rows={[
            ["Wants notifications", user.isNotificationEnabled ? "Yes" : "No"],
            [
              "Phone allows them",
              user.pushPermission
                ? `${humanize(user.pushPermission)}, as of ${formatDay(user.pushPermissionAt)}`
                : "Never reported",
            ],
            [
              "Notifications",
              `${n(summary?.notifications)} sent, ${n(summary?.notificationsUnread)} unread`,
            ],
            ["Texts", n(summary?.textsSent)],
            [
              "WhatsApp",
              `${n(summary?.whatsAppSent)} sent, ${n(summary?.whatsAppReceived)} received`,
            ],
            [
              "Devices",
              summary?.devices?.length
                ? summary.devices.map((device: any, index: number) => (
                    <Box key={index} component="span" sx={{ display: "block" }}>
                      {[device.timeZone, device.locale]
                        .filter(Boolean)
                        .join(", ") || "Details not reported"}
                      <Typography
                        component="span"
                        variant="caption"
                        color="text.secondary"
                      >
                        {" "}
                        · updated {formatDay(device.lastUpdatedAt)}
                      </Typography>
                    </Box>
                  ))
                : "None registered",
            ],
          ]}
        />
      </Section>

      {summary?.onboarding && (
        <Section title="What they told us">
          <Facts
            rows={[
              ["What brought them", humanize(summary.onboarding.goal)],
              [
                "How they heard",
                [
                  humanize(summary.onboarding.heardAbout),
                  summary.onboarding.heardAboutDetail,
                ]
                  .filter(Boolean)
                  .join(": "),
              ],
            ]}
          />
        </Section>
      )}
    </Stack>
  </Box>
);

/**
 * One member's page: who they are, what they have done and what we have sent
 * them. Backend reference: docs/USER_DETAILS_PAGE.md in ilbp-backend.
 *
 * The open tab is in the URL so a link opens it. Only the open tab queries, and
 * nothing polls: the admin's budget counts every root field.
 */
export const UserDetails: React.FC = (): JSX.Element => {
  const params = useParams();
  const [search, setSearch] = useSearchParams();
  const userId = parseUserId(params.userId);
  const tab = parseTab(search.get("tab"));

  const { data, loading, error } = useQuery(USER_DETAILS_PAGE, {
    variables: { id: userId },
    skip: userId === null,
    fetchPolicy: "cache-and-network",
  });
  const {
    data: summaryData,
    loading: summaryLoading,
    error: summaryError,
  } = useQuery(USER_SUMMARY, {
    variables: { userId },
    skip: userId === null,
    fetchPolicy: "cache-and-network",
  });

  const open = (next: UserTab) =>
    setSearch(next === "overview" ? {} : { tab: next }, { replace: true });

  const backToUsers = (
    <Link component={RouterLink} to="/users" underline="hover" variant="body2">
      ← All users
    </Link>
  );

  if (userId === null) {
    return (
      <Stack spacing={2}>
        {backToUsers}
        <Alert severity="warning">This is not a user id.</Alert>
      </Stack>
    );
  }

  const user = data?.fetchUserDetails;
  const summary = summaryData?.adminUserSummary;

  // The summary answers first with a clear "no such user"; the other only says a field was null.
  const missing =
    summaryError?.graphQLErrors?.[0]?.extensions?.code === "ACCOUNT_NOT_FOUND";

  if (!user && (loading || summaryLoading)) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress aria-label="Loading this user" />
      </Box>
    );
  }

  if (!user) {
    return (
      <Stack spacing={2}>
        {backToUsers}
        {missing ? (
          <Alert severity="warning">No user has this id.</Alert>
        ) : (
          <Alert severity="error">
            {describeError(error, "Could not load this user.")}
          </Alert>
        )}
      </Stack>
    );
  }

  return (
    <Stack spacing={2}>
      <Paper variant="outlined" sx={{ px: 2, pt: 2 }}>
        {backToUsers}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            mt: 1,
            flexWrap: "wrap",
          }}
        >
          <Avatar
            src={user.ProfileImage?.url}
            alt=""
            sx={(theme) => ({
              width: 64,
              height: 64,
              fontWeight: 600,
              bgcolor: alpha(theme.palette.primary.main, 0.1),
              color: "primary.main",
            })}
          >
            {initialsOf(user)}
          </Avatar>
          <Box sx={{ minWidth: 0, flex: "1 1 240px" }}>
            <Typography
              variant="h4"
              component="h1"
              sx={{ wordBreak: "break-word" }}
            >
              {nameOf(user)}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ wordBreak: "break-word" }}
            >
              @{user.username}
              {locationText(user) ? ` · ${locationText(user)}` : ""} · Joined{" "}
              {formatDay(user.createdAt)}
            </Typography>
            <Box
              sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mt: 0.75 }}
            >
              {accountChips(user).map((chip) => (
                <Chip
                  key={chip.label}
                  size="small"
                  variant="outlined"
                  color={chip.color}
                  label={chip.label}
                />
              ))}
            </Box>
          </Box>
          {summary?.crmContactId && (
            <Button
              component={RouterLink}
              to={contactPath(summary.crmContactId)}
              size="small"
              variant="outlined"
              sx={{ flexShrink: 0 }}
            >
              Open in CRM
            </Button>
          )}
        </Box>

        <Tabs
          value={tab}
          onChange={(_, next) => open(next)}
          variant="scrollable"
          allowScrollButtonsMobile
          sx={{ mt: 1 }}
        >
          {USER_TABS.map((item) => (
            <Tab key={item.key} value={item.key} label={item.label} />
          ))}
        </Tabs>
      </Paper>

      {user.isBanned && (
        <Alert severity="error">
          <AlertTitle>
            Banned{user.bannedAt ? ` on ${formatDay(user.bannedAt)}` : ""}
          </AlertTitle>
          {user.banReason || "No reason was recorded."}
          {user.bannedBy ? ` Banned by ${user.bannedBy}.` : ""}
        </Alert>
      )}
      {!user.isBanned && user.isDeleted && (
        <Alert severity="warning">This person deleted their account.</Alert>
      )}

      {summaryError && !summary && (
        <Alert severity="warning">
          {describeError(summaryError, "Could not load this person's numbers.")}
        </Alert>
      )}

      <TileRow>
        <StatTile
          label="Recommendations"
          value={summary ? n(summary.recommendations) : "…"}
          caption={
            summary
              ? `${n(summary.recommendationsWithdrawn)} withdrawn`
              : undefined
          }
          onClick={() => open("recommendations")}
        />
        <StatTile
          label="Reviews"
          value={summary ? n(summary.reviews) : "…"}
          caption={summary ? `${n(summary.reviewsVisible)} visible` : undefined}
          onClick={() => open("reviews")}
        />
        <StatTile
          label="Referrals"
          value={summary ? n(summary.referrals) : "…"}
          caption={
            summary?.referredBy
              ? `Referred by ${summary.referredBy.name}`
              : undefined
          }
          onClick={() => open("referrals")}
        />
        <StatTile
          label="Points"
          value={n(user.totalPoints)}
          caption={
            summary
              ? countOf(summary.achievementsCompleted, "achievement")
              : undefined
          }
          onClick={() => open("points")}
        />
        <StatTile
          label="App opens"
          value={summary ? n(summary.appOpens) : "…"}
          caption={
            summary
              ? `${n(summary.activeDaysLast30)} of the last 30 days`
              : undefined
          }
          onClick={() => open("activity")}
        />
        <StatTile
          label="Last seen"
          value={
            summary
              ? sinceText(summary.lastSeenAt, new Date().toISOString()) ||
                "Never"
              : "…"
          }
          valueSize={20}
          caption={
            summary?.lastSeenAt ? formatMoment(summary.lastSeenAt) : undefined
          }
          onClick={() => open("activity")}
        />
      </TileRow>

      {tab === "overview" && <Overview user={user} summary={summary} />}
      {tab === "recommendations" && <UserRecommendations userId={userId} />}
      {tab === "activity" && <UserActivityFeed userId={userId} />}
      {tab === "whatsapp" && <UserWhatsApp userId={userId} />}
      {tab === "reviews" && <UserReviews userId={userId} />}
      {tab === "referrals" && <UserReferrals userId={userId} />}
      {tab === "badges" && <UserBadges user={user} />}
      {tab === "points" && (
        <UserPoints username={user.username} totalPoints={user.totalPoints} />
      )}
    </Stack>
  );
};
